import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
	Calendar,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Download,
	Search,
} from "lucide-react";

import { getUsers, getVisits } from "../config/url";
import { getConfigWithToken } from "../config/config";
import {
	formatDateUS,
	formatDurationLabel,
	formatFlagLabel,
	formatTimeUS,
	formatVisitDisplayId,
	getClientName,
	getConfirmationStatus,
	getFlagCount,
	getGpsStatus,
	getProviderName,
	getVisitStatus,
} from "../utils/visitDisplay";
import {
	buildVisitQueryParams,
	DATE_RANGE_OPTIONS,
	hasActiveVisitFilters,
} from "../utils/visitDateFilters";
import classes from "./Visits.module.css";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const SKELETON_ROWS = 8;

const Visits = () => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId") ||
		"";
	const navigate = useNavigate();

	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [providerFilter, setProviderFilter] = useState("");
	const [dateRangeFilter, setDateRangeFilter] = useState("");
	const [customStartDate, setCustomStartDate] = useState("");
	const [customEndDate, setCustomEndDate] = useState("");
	const [visits, setVisits] = useState([]);
	const [providers, setProviders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [totalPages, setTotalPages] = useState(1);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Cache-Control": "no-cache",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearch, statusFilter, providerFilter, dateRangeFilter, customStartDate, customEndDate, rowsPerPage]);

	useEffect(() => {
		const loadProviders = async () => {
			try {
				const response = await axios.get(getUsers, {
					...requestConfig,
					params: {
						limit: 200,
						page: 1,
						status: "Active",
						...(organizationId ? { organizationId } : {}),
						role: "Provider",
					},
				});
				const rows = response?.data?.data || [];
				const list = (Array.isArray(rows) ? rows : [])
					.map((user) => ({
						id: user._id,
						name:
							[user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
							user.userId ||
							user.email ||
							"Provider",
					}))
					.filter((provider) => provider.id)
					.sort((a, b) => a.name.localeCompare(b.name));
				setProviders(list);
			} catch {
				setProviders([]);
			}
		};
		loadProviders();
	}, [organizationId, requestConfig]);

	const loadVisits = useCallback(async () => {
		if (!organizationId) {
			setVisits([]);
			setTotalPages(1);
			setLoading(false);
			setError("Organization context is required.");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(getVisits, {
				...requestConfig,
				params: buildVisitQueryParams({
					currentPage,
					dateRange: dateRangeFilter,
					debouncedSearch,
					endDate: customEndDate,
					organizationId,
					providerFilter,
					rowsPerPage,
					startDate: customStartDate,
					statusFilter,
				}),
			});

			if (response.data?.success === false) {
				throw new Error(response.data?.message || "Failed to load visits.");
			}

			const rows = response?.data?.data || [];
			const pagination = response?.data?.pagination || {};
			setVisits(Array.isArray(rows) ? rows : []);
			setTotalPages(pagination.totalPages || 1);
		} catch (err) {
			setVisits([]);
			setTotalPages(1);
			setError(
				err?.response?.data?.message ||
					err?.message ||
					"Unable to load community visits. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	}, [
		currentPage,
		customEndDate,
		customStartDate,
		dateRangeFilter,
		debouncedSearch,
		organizationId,
		providerFilter,
		requestConfig,
		rowsPerPage,
		statusFilter,
	]);

	useEffect(() => {
		loadVisits();
	}, [loadVisits]);

	const hasFilters = hasActiveVisitFilters({
		dateRange: dateRangeFilter,
		debouncedSearch,
		endDate: customEndDate,
		providerFilter,
		startDate: customStartDate,
		statusFilter,
	});

	const handleDateRangeChange = (value) => {
		setDateRangeFilter(value);
		if (value !== "custom") {
			setCustomStartDate("");
			setCustomEndDate("");
		}
	};

	const openVisit = (visit) => {
		const visitId = visit?.id || visit?.visit_id;
		if (!visitId) return;
		navigate(`/visits/${visitId}`, { state: { visit } });
	};

	const handleRowKeyDown = (event, visit) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			openVisit(visit);
		}
	};

	const renderStatusBadge = (visit) => {
		const status = getVisitStatus(visit);
		if (status === "Verified") {
			return (
				<span className={classes.statusVerified}>
					<span aria-hidden="true" className={classes.statusDot} />
					Verified
				</span>
			);
		}
		if (status === "Flagged") {
			return (
				<span className={classes.statusFlagged}>
					<span aria-hidden="true" className={classes.statusDot} />
					Flagged
				</span>
			);
		}
		return (
			<span className={classes.statusIncomplete}>
				<span aria-hidden="true" className={classes.statusDot} />
				Incomplete
			</span>
		);
	};

	const renderConfirmation = (visit) => {
		const status = getConfirmationStatus(visit);
		if (status === "Confirmed") return <span className={classes.confirmed}>Confirmed</span>;
		if (status === "Unconfirmed") return <span className={classes.unconfirmed}>Unconfirmed</span>;
		return <span className={classes.mutedCell}>—</span>;
	};

	const renderGps = (visit) => {
		const status = getGpsStatus(visit);
		if (status === "Valid") return <span className={classes.gpsValid}>Valid</span>;
		if (status === "Invalid") return <span className={classes.gpsInvalid}>Invalid</span>;
		return <span className={classes.mutedCell}>—</span>;
	};

	const renderFlags = (visit) => {
		const count = getFlagCount(visit);
		if (!count) return <span className={classes.mutedCell}>—</span>;
		const label = formatFlagLabel(visit);
		if (count === 1) return <span className={classes.flagText}>{label}</span>;
		return <span className={`${classes.flagBadge} ${count > 1 ? classes.flagBadgeMany : ""}`}>{label}</span>;
	};

	const renderTableBody = () => {
		if (loading) {
			return Array.from({ length: SKELETON_ROWS }, (_, index) => (
				<tr aria-hidden="true" className={classes.skeletonRow} key={`skeleton-${index}`}>
					{Array.from({ length: 11 }).map((__, cellIndex) => (
						<td key={cellIndex}>
							<div className={classes.skeletonCell} />
						</td>
					))}
				</tr>
			));
		}

		if (visits.length === 0) {
			return (
				<tr>
					<td className={classes.emptyCell} colSpan={11}>
						<p className={classes.emptyTitle}>{hasFilters ? "No matching visits" : "No visits found"}</p>
						<p className={classes.stateText}>
							{hasFilters
								? "Try adjusting your search or filters."
								: "Community visit records will appear here once visits are recorded for your organization."}
						</p>
					</td>
				</tr>
			);
		}

		return visits.map((visit) => (
			<tr
				className={classes.row}
				key={visit.id || visit.visit_id}
				onClick={() => openVisit(visit)}
				onKeyDown={(event) => handleRowKeyDown(event, visit)}
				role="link"
				tabIndex={0}>
				<td className={classes.visitId}>{formatVisitDisplayId(visit)}</td>
				<td className={classes.clientName}>{getClientName(visit)}</td>
				<td className={classes.providerName}>{getProviderName(visit)}</td>
				<td>{formatDateUS(visit.date || visit.created_at)}</td>
				<td>{formatTimeUS(visit.time_in || visit.timeIn)}</td>
				<td>{formatTimeUS(visit.time_out || visit.timeOut)}</td>
				<td className={classes.durationCell}>{formatDurationLabel(visit)}</td>
				<td>{renderConfirmation(visit)}</td>
				<td>{renderGps(visit)}</td>
				<td>{renderFlags(visit)}</td>
				<td>{renderStatusBadge(visit)}</td>
			</tr>
		));
	};

	return (
		<div className={classes.page}>
			<div className={classes.pageHeader}>
				<div className={classes.titleBlock}>
					<h1 className={classes.title}>Community Visits</h1>
					<p className={classes.subtitle}>GPS-based community service verification records.</p>
				</div>
				<button
					className={classes.exportButton}
					onClick={() => toast.info("Visit export will be available soon.")}
					type="button">
					<Download size={15} strokeWidth={2} />
					Export
				</button>
			</div>

			<div className={classes.toolbar}>
				<div className={classes.filters}>
					<div className={classes.searchWrap}>
						<Search aria-hidden="true" className={classes.searchIcon} size={15} strokeWidth={2} />
						<input
							aria-label="Search visits"
							className={classes.searchInput}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search visits..."
							type="search"
							value={search}
						/>
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by visit status"
							className={classes.filterSelect}
							onChange={(event) => setStatusFilter(event.target.value)}
							value={statusFilter}>
							<option value="">All Statuses</option>
							<option value="Verified">Verified</option>
							<option value="Flagged">Flagged</option>
							<option value="Incomplete">Incomplete</option>
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by provider"
							className={classes.filterSelect}
							onChange={(event) => setProviderFilter(event.target.value)}
							value={providerFilter}>
							<option value="">All Providers</option>
							{providers.map((provider) => (
								<option key={provider.id} value={provider.id}>
									{provider.name}
								</option>
							))}
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by date range"
							className={classes.filterSelect}
							onChange={(event) => handleDateRangeChange(event.target.value)}
							value={dateRangeFilter}>
							<option value="">Date Range</option>
							{DATE_RANGE_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>

					{dateRangeFilter === "custom" ? (
						<div className={classes.dateRangeField}>
							<Calendar aria-hidden="true" className={classes.dateRangeIcon} size={15} strokeWidth={2} />
							<input
								aria-label="Custom range start date"
								className={classes.dateRangePart}
								onChange={(event) => setCustomStartDate(event.target.value)}
								type="date"
								value={customStartDate}
							/>
							<span className={classes.dateRangeSeparator}>–</span>
							<input
								aria-label="Custom range end date"
								className={classes.dateRangePart}
								min={customStartDate || undefined}
								onChange={(event) => setCustomEndDate(event.target.value)}
								type="date"
								value={customEndDate}
							/>
						</div>
					) : null}
				</div>
			</div>

			<div className={classes.card}>
				{error ? (
					<div className={classes.errorState} role="alert">
						<p className={classes.errorTitle}>Unable to load community visits</p>
						<p className={classes.stateText}>{error}</p>
						<button className={classes.retryButton} onClick={loadVisits} type="button">
							Try Again
						</button>
					</div>
				) : (
					<>
						<div className={classes.tableWrap}>
							<table aria-busy={loading} className={classes.table}>
								<thead>
									<tr>
										<th>Visit ID</th>
										<th>Client</th>
										<th>Provider</th>
										<th>Date</th>
										<th>Time In</th>
										<th>Time Out</th>
										<th>Duration</th>
										<th>Confirmation</th>
										<th>GPS</th>
										<th>Flags</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>{renderTableBody()}</tbody>
							</table>
						</div>

						<div className={classes.pagination}>
							<label className={classes.rowsPerPage}>
								<span>Rows per page</span>
								<div className={classes.rowsSelectWrap}>
									<select
										aria-label="Rows per page"
										className={classes.rowsSelect}
										disabled={loading}
										onChange={(event) => setRowsPerPage(Number(event.target.value))}
										value={rowsPerPage}>
										{ROWS_PER_PAGE_OPTIONS.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
									<ChevronDown
										aria-hidden="true"
										className={classes.rowsSelectIcon}
										size={14}
										strokeWidth={2}
									/>
								</div>
							</label>

							<div className={classes.pageControls}>
								<button
									aria-label="Previous page"
									className={classes.pageButton}
									disabled={loading || currentPage <= 1}
									onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
									type="button">
									<ChevronLeft size={18} strokeWidth={2} />
								</button>
								<span className={classes.pageLabel}>
									Page {currentPage} of {totalPages}
								</span>
								<button
									aria-label="Next page"
									className={classes.pageButton}
									disabled={loading || currentPage >= totalPages}
									onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
									type="button">
									<ChevronRight size={18} strokeWidth={2} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default Visits;
